
/* eslint-disable react/no-unused-prop-types */
import React, { useMemo, useState } from 'react';
import _isEmpty from 'lodash.isempty';
import _get from 'lodash.get';
import _omit from 'lodash.omit';
import { ADDON_TYPE } from '@shootsta/common-domain-data';
import { getVoiceOverDuration, getEstimatedVoiceOverCost } from '../../Addons';
import { List, Text } from '../../../ui-kit';
import LANGUAGES from '../constants/languages';
import VoiceOverListItem from './VoiceOverListItem';
import { getCustomCostForAddon } from '../../Addons/utils/getCost';
import hasCustomVoiceOverArtistCost from '../../Addons/utils/hasCustomVoiceOverArtistCost';
import '../styles/_manage-voice-over.scss';
















const CHUNK_SIZE = 20;

export const SORT_BY = [
{ label: 'Name: A-Z', value: 'aToz' },
{ label: 'Name: Z-A', value: 'zToa' },
{ label: 'Newest', value: 'createdAtDesc' },
{ label: 'Oldest', value: 'createdAtAsc' }];


export const MOCK_ADVANCED_FILTERS = [
{
  type: 'CHECKBOX',
  name: 'gender',
  label: 'Gender',
  filterFacets: [
  { label: 'Male', value: 'MALE' },
  { label: 'Female', value: 'FEMALE' }]

},
{
  type: 'CHECKBOX',
  name: 'languages',
  label: 'Languages',
  filterFacets: LANGUAGES
},
{
  type: 'CHECKBOX',
  name: 'ageRange',
  label: 'Age Range',
  filterFacets: [
  { label: 'Child (0-12)', value: 'CHILD' },
  { label: 'Teenager (13-17)', value: 'TEENAGER' },
  { label: 'Yng Adult (18-29)', value: 'YNG_ADULT' },
  { label: 'Adult (30-50)', value: 'ADULT' },
  { label: 'Older Sound (50+)', value: 'OLDEST' }]

}];


const initialFilterState = {
  where: {
    status: ['PUBLISHED'],
    targetWorkspace: ['FREE']
  },
  order: 'aToz',
  offset: 0
};

function ManageVoiceOverArtists({
  fetchAssets,
  assetsMap,
  getFilteredVoiceOverArtists,
  selectedVoiceOverArtists,
  getBillingEntityCode,
  onSelect,
  scriptWordCount,
  containerWidth,
  priceVersion = '',
  isV2Design,
  customAddonCosts = [],
  isShootstaUser = false
}) {
  const [loadingMore, setLoadingMore] = useState(false);
  const [count, setCount] = useState(0);
  const [filters, setFilters] = useState(initialFilterState);
  const [voiceOverArtists, setVoiceOverArtists] = useState([]);
  const billingEntityCode = useMemo(
    () => getBillingEntityCode ? getBillingEntityCode() : '',
    [getBillingEntityCode]
  );

  const getCost = () => {
    if (hasCustomVoiceOverArtistCost(customAddonCosts)) {
      return getCustomCostForAddon({
        addonType: ADDON_TYPE.VOICE_OVER_ARTIST,
        customAddonCosts,
        billingEntityCode: billingEntityCode || 'AUS'
      });
    }

    return getEstimatedVoiceOverCost({
      voiceOverArtistsCount: 1,
      wordCount: scriptWordCount,
      billingEntityCode,
      priceVersion
    });
  };

  const getArtistWithCost = ({ voiceOverArtistId, artistName }) => ({
    artistId: voiceOverArtistId,
    name: artistName,
    value: true,
    createdAt: new Date().toISOString(),
    duration: getVoiceOverDuration(scriptWordCount),
    cost: getCost()
  });

  const handleSelect = (artist) => {
    const { voiceOverArtistId } = artist;
    const isExists = selectedVoiceOverArtists.some(
      (it) => it.artistId === voiceOverArtistId
    );

    const updatedSelectedVOArtists = isExists ?
    selectedVoiceOverArtists.filter(
      ({ artistId }) => artistId !== voiceOverArtistId
    ) :
    [...selectedVoiceOverArtists, getArtistWithCost(artist)];

    const updatedSelectedVOArtistsWithDetails = updatedSelectedVOArtists.map(
      (it) => {
        const VoiceOverArtistDetails = voiceOverArtists.find(
          ({ voiceOverArtistId: id }) => id === it.artistId
        );
        const { languages, artistName, voiceOver, customThumbnail } = VoiceOverArtistDetails || {};
        return {
          ...it,
          name: artistName,
          languages,
          voiceOver,
          customThumbnail
        };
      }
    );

    onSelect(updatedSelectedVOArtistsWithDetails);
  };

  const getVoiceOverArtists = async (payload = {}) => {
    const { data, error } = await getFilteredVoiceOverArtists(payload);
    if (error) {
      return null;
    }

    setCount(_get(data, 'getFilteredVoiceOverArtists.count', 0));
    const filteredVoiceOverArtists = _get(
      data,
      'getFilteredVoiceOverArtists.VoiceOverArtist',
      []
    );
    setVoiceOverArtists((prevArtists) => {
      if (payload.offset) {
        return [...prevArtists, ...filteredVoiceOverArtists];
      }
      return filteredVoiceOverArtists;
    });

    const assetsToLoad = filteredVoiceOverArtists.
    map(({ voiceOver, customThumbnail }) => [voiceOver, customThumbnail]).
    flat().
    filter(Boolean);

    if (_isEmpty(assetsToLoad)) {
      return null;
    }

    await fetchAssets({ assetIds: assetsToLoad });
    return filteredVoiceOverArtists;
  };
  const onAdvancedFiltersChange = (selectedFilters = {}) => {
    const updatedWhereFilters = _isEmpty(selectedFilters) ?
    initialFilterState.where :
    {
      ...filters.where,
      ...selectedFilters
    };
    const filtered = _omit(updatedWhereFilters, 'sortBy');
    const updatedFilters = {
      where: filtered,
      order: filters.order,
      offset: 0 // reset the offset value to its default when a new filter is selected.
    };
    setFilters(updatedFilters);
    getVoiceOverArtists(updatedFilters);
  };

  const onSortChange = async (newFilters = {}) => {
    const updatedFilters = {
      ...filters,
      order: newFilters.filters.sortBy,
      offset: 0, // reset the offset value to its default when a new filter is selected.
      where: {
        ...filters.where,
        searchTerm: newFilters.searchTerm
      }
    };

    setFilters(updatedFilters);

    if (_isEmpty(updatedFilters)) {
      return;
    }

    await getVoiceOverArtists(updatedFilters);
  };

  const loadMoreResults = async () => {
    setLoadingMore(true);

    const updatedFilters = {
      ...filters,
      offset: filters.offset + CHUNK_SIZE
    };
    setFilters(updatedFilters);
    await getVoiceOverArtists(updatedFilters);
    setLoadingMore(false);
  };

  const renderVoiceOver = (artist) =>
  <VoiceOverListItem
    key={artist.voiceOverArtistId}
    artist={artist}
    assetsMap={assetsMap}
    checked={
    selectedVoiceOverArtists &&
    selectedVoiceOverArtists.some(
      (it) => it.artistId === artist.voiceOverArtistId
    )}

    handleSelectVoiceOverArtist={handleSelect}
    canUpdateVideo
    isV2Design={isV2Design}
    isShootstaUser={isShootstaUser} />;



  const getListProps = () => {
    if (isV2Design) {
      return {
        alignSearchRight: true,
        filtersLeftAddon:
        <Text className="manage-voice-over-artist__title" noMargin>
            Add a professional voice
          </Text>

      };
    }
    return {
      isFixedFilters: true
    };
  };

  return (
    <div className="manage-voice-over-artist">
      <List
        renderListItem={renderVoiceOver}
        data={voiceOverArtists}
        filters={{ sortBy: SORT_BY }}
        onFiltersChange={onSortChange}
        advancedFilters={MOCK_ADVANCED_FILTERS}
        onAdvancedFiltersChange={onAdvancedFiltersChange}
        isCollapsibleAdvancedFilters
        isSearchBarCollapsed
        loadingMore={loadingMore}
        loadMoreResults={loadMoreResults}
        totalResults={count}
        containerWidth={containerWidth}
        {...getListProps()} />

    </div>);

}

export default ManageVoiceOverArtists;